/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Scene from "js_interface/src/scene_interface";

import Factory from "App/geometry/material/factory";
import AbstractMaterial from "App/geometry/material/abstract_material";
import SurfaceColor from "App/geometry/material/surface_color";
import RenderOption from "App/geometry/material/render_option";
import LineProperties from "App/geometry/material/line_properties";
import PointProperties from "App/geometry/material/point_properties";
import {Texture, EDataTypeType} from "App/geometry/material/texture";
import Description from "App/model/description";

/***
 * We take the objects (surface colors, render options etc. and extend their id's with model_id prefix
 *
 * @param aSceneAPI
 * @constructor
 */
export default
class MaterialLibrary {

    private static cwgMfV: any = {
        object: null,
        key: null,
        potentiallyMatchingObjects: null,
        i: null,
        category: null
    };

    private _sceneAPI: Scene;

    constructor(aSceneAPI: Scene) {

        this._sceneAPI = aSceneAPI;

        // create material category as members of this class
        let categories = MaterialLibrary.getCategories();
        for (let category of categories) {
            this[category] = {
                itemsByID: {}, // material objects in map with material uid as key
                itemsByHash: {}, // material objects in map sorted by hash key for speedup matching newly added material (used for textures now with url[0] as hash)
                parsedIdToUid: {}
            };
        }
    }

    /***
     * A Map used as type. It defines what members (material category) are in material library and what type of material
     * type is assigned to particular categories.
     * When adding new material category and type, please add support for parsing the type to Appearance.parse (for old models)
     * @type {{surfaceColors: [*], renderOptions: [*], pointProperties: [*], lineProperties: [*], textures: [*]}}
     */
    public static getCategories() {
        return [
            "surfaceColors",
            "renderOptions",
            "pointProperties",
            "lineProperties",
            "textures"
        ];
    }

    public static getMaterialMembers() {
        return [
            "surfaceColor",
            "renderOption",
            "pointProperties",
            "lineProperties",
            "texture"
        ];
    }

    public static getPositionInMaterialRefArray(aCategory)
    {
        switch (aCategory) {
            case "surfaceColors":
                return 0;
                break;
            case "renderOptions":
                return 1;
                break;
            case "pointProperties":
                return 2;
                break;
            case "lineProperties":
                return 3;
                break;
            case "textures":
                return 4;
                break;
            default:
                return 0;
        }
    }

    public getUidFromParsedId(aCategory, aParsedId): number
    {
        return this[aCategory].parsedIdToUid[aParsedId];
    }

    public createObjectFromJSON(aModelDescription: Description, aJSON: any, aType: string, aHash: string): AbstractMaterial
    {
        let object = Factory.create(aType, aHash);
        object.parse(aModelDescription, aJSON);
        return object;
    }

    /***
     *
     * @param aModelDescription
     * @param aObjectJSON
     * @param aType
     * @param aMaterialCategory
     * @param aHash Hash is here to sort object to smaller groups
     * @returns {*}
     * @private
     */

    public _setOrCreateMaterialObject(aModelDescription: Description, aObjectJSON: any, aType: string, aMaterialCategory: string, aHash: string): AbstractMaterial
    {
        let matchedObject = this.matchObject(aModelDescription, aObjectJSON, aMaterialCategory, aHash);
        if (matchedObject) {
            return matchedObject;
        }
        else {
            let newObject = this.createObjectFromJSON(aModelDescription, aObjectJSON, aType, aHash);
            if (newObject.getID() !== null) {
                this[aMaterialCategory].parsedIdToUid[newObject.getID()] = newObject.getUID();
            }

            this.addObject(
                newObject,
                aMaterialCategory
            );

            return newObject;
        }
    }

    public _parseSurfaceColor(aModelDescription: Description, aJSON: any): AbstractMaterial
    {
        let object: any = SurfaceColor.getDefaultValues(); // start with default values
        if (aJSON !== undefined) { // load surface material props if defined
            for (let property in aJSON) {
                object[property] = aJSON[property];
            }
        }

        return this._setOrCreateMaterialObject(
            aModelDescription,
            object,
            SurfaceColor.TypeName,
            "surfaceColors",
            null
        );
    }

    public _parseRenderOption(aModelDescription: Description, aJSON: any): AbstractMaterial
    {
        let object: any = RenderOption.getDefaultValues(); // start with default values
        if (aJSON !== undefined) { // load render options props if defined
            for (let property in aJSON) {
                object[property] = aJSON[property];
            }
        }

        // inject modelUID property to render option (done in rneder node
        // object["modelUID"] = aModelDescription.getRenderModel().getUID();

        return this._setOrCreateMaterialObject(
            aModelDescription,
            object,
            RenderOption.TypeName,
            "renderOptions",
            null
        );
    }

    public _parsePointProperties(aModelDescription: Description, aJSON: any): AbstractMaterial
    {
        if (aJSON === undefined) {
            return null;
        }

        let object = PointProperties.getDefaultValues(); // start with default values
        for (let property in aJSON) {
            object[property] = aJSON[property];
        }

        return this._setOrCreateMaterialObject(
            aModelDescription,
            object,
            PointProperties.TypeName,
            "pointProperties",
            null
        );
    }

    public _parseLineProperties(aModelDescription: Description, aJSON: any)
    {
        if (aJSON === undefined) {
            return null;
        }

        let object = LineProperties.getDefaultValues(); // start with default values
        for (let property in aJSON) {
            object[property] = aJSON[property];
        }

        return this._setOrCreateMaterialObject(
            aModelDescription,
            object,
            LineProperties.TypeName,
            "lineProperties",
            null
        );
    }

    public _parseTexture(aModelDescription: Description, aJSON: any): AbstractMaterial
    {
        let texturesCompression: any = this._sceneAPI.renderSettingsModule.getTexturesCompressionEnabled();
        let hash = null;
        let object: any = {};
        object.dataType = EDataTypeType.imageTexture; // set default compression type

        if (aJSON.hasOwnProperty("id")) {
            object.id = aJSON.id; // because of matrial library
        }

        for (let key in EDataTypeType) {
            if (aJSON.hasOwnProperty(key)) {
                let textureData = Texture.getDefaultValues();

                // extend/change the default values by the values from the input json
                let imageJSON = aJSON[key];
                for (let property in imageJSON) {
                    if (imageJSON[property] instanceof Array) {
                        textureData[property] = [];
                        for (let i = 0; i < imageJSON[property].length; i++) {
                            textureData[property].push(imageJSON[property][i]);
                        }
                    }
                    else {
                        textureData[property] = imageJSON[property];
                    }
                }
                object[key] = textureData;

                // set active compression type and compute hash for fast matching textures
                if (texturesCompression.type === key) {
                    object.dataType = key;
                }
                hash = textureData.url[0];
            }
        }

        // when crn image is available, use it instead of dds
        if (object.hasOwnProperty(EDataTypeType.crunchImageTexture) &&
            texturesCompression.type === EDataTypeType.ddsImageTexture) {
            object.dataType = EDataTypeType.crunchImageTexture;
        }

        return this._setOrCreateMaterialObject(
            aModelDescription,
            object,
            Texture.TypeName,
            "textures",
            hash);
    }

    public parse(aModelDescription: Description, aMaterialLibraryJSON: object)
    {
        // parse surface colors
        let key = "surfaceColors";
        if (aMaterialLibraryJSON.hasOwnProperty(key)) {
            for (let surfaceColorJSON of aMaterialLibraryJSON[key]) {
                this._parseSurfaceColor(aModelDescription, surfaceColorJSON);
            }
        }

        key = "renderOptions";
        if (aMaterialLibraryJSON.hasOwnProperty(key)) {
            for (let renderOptionJSON of aMaterialLibraryJSON[key]) {
                this._parseRenderOption(aModelDescription, renderOptionJSON);
            }
        }

        key = "pointProperties";
        if (aMaterialLibraryJSON.hasOwnProperty(key)) {
            for (let pointPropertiesJSON of aMaterialLibraryJSON[key]) {
                this._parsePointProperties(aModelDescription, pointPropertiesJSON);
            }
        }

        key = "lineProperties";
        if (aMaterialLibraryJSON.hasOwnProperty(key)) {
            for (let linePropertiesJSON of aMaterialLibraryJSON[key]) {
                this._parseLineProperties(aModelDescription, linePropertiesJSON);
            }
        }

        key = "textures";
        if (aMaterialLibraryJSON.hasOwnProperty(key)) {
            for (let i = 0; i < aMaterialLibraryJSON[key].length; i++) {
                let textureJson: any = {};
                textureJson.id = i;
                for (let ttKey in EDataTypeType) {
                    if (aMaterialLibraryJSON[key][i].hasOwnProperty(ttKey)) {
                        textureJson[ttKey] = aMaterialLibraryJSON[key][i][ttKey];
                    }
                }

                this._parseTexture(aModelDescription, textureJson);
            }
        }
    }

    public addObject(aObject: AbstractMaterial, aType: string)
    {
        let  hash = aObject.getHashKey();
        if (hash) {
            let category = this[aType];
            if (category.itemsByHash[hash] === undefined) {
                category.itemsByHash[hash] = [];
            }

            category.itemsByHash[hash].push(aObject);
            category.itemsByID[aObject.getUID()] = aObject; // for textres loaded from material library
        }
        else {
            this[aType].itemsByID[aObject.getUID()] = aObject;
        }
    }

    public removeObject(aObject: AbstractMaterial, aType: string)
    {
        let category = this[aType];
        let hash = aObject.getHashKey();
        let uid = aObject.getUID();
        if (hash) {
            let objects = category.itemsByHash[hash];
            for (let i = 0; i < objects.length; i++) {
                if (objects[i].getUID() === uid) {
                    objects.slice(i, 1);
                    break;
                }
            }
        }
        else if (category.itemsByID[uid] !== undefined) {
            delete category.itemsByID[uid];
        }
    }

    public getObjectByParsedID(aID: number, aType: string): AbstractMaterial
    {
        if (aID === -1 || aID === undefined) {
            return null;
        }

        let object = this[aType].itemsByID[aID];
        if (object !== undefined) {
            return object;
        }

        return null;
    }

    public matchObject(aModelDescription: Description, aObjectJson: any, aType: string, aHash: string): AbstractMaterial
    {
        if (aHash) {
            MaterialLibrary.cwgMfV.potentiallyMatchingObjects = this[aType].itemsByHash[aHash];
            if (MaterialLibrary.cwgMfV.potentiallyMatchingObjects === undefined) {
                return null;
            }

            for (let testedObject of MaterialLibrary.cwgMfV.potentiallyMatchingObjects) {
                if (testedObject.match(aModelDescription, aObjectJson)) {
                    return testedObject;
                }
            }

            return null;
        }

        // match in items by id when hash is not used
        MaterialLibrary.cwgMfV.category = this[aType].itemsByID;
        for (let key in MaterialLibrary.cwgMfV.category) {
            MaterialLibrary.cwgMfV.object = MaterialLibrary.cwgMfV.category[key];

            if (MaterialLibrary.cwgMfV.object.match(aModelDescription, aObjectJson)) {
                return MaterialLibrary.cwgMfV.object;
            }
        }

        return null;
    }

    public getJSONRepresentation(): any
    {
        // save all materials from all containers
        // this is not necessary as we can create the materials from the input values
        return {};
    }
}
